import { z } from 'zod';

import { jobProfileComponentSchema } from '~/shared/data-contract/job-profile';

export const translationTextSchema = z.object({
    locale: z.string(),
    text: z.string(),
});

export type TranslationText = z.infer<typeof translationTextSchema>;

export const SKILL_DETECTION_PREFERENCE_MAX_LENGTH = 5000;
export const TASK_CONTENT_GUIDANCE_MAX_LENGTH = 5000;

export const skillLimitsSchema = z.object({
    expectedNumberOfSkills: z.number().min(5).max(50),
    maximumNumberOfSkills: z.number().min(5).max(70),
    skillDetectionPreference: z.string().max(SKILL_DETECTION_PREFERENCE_MAX_LENGTH).nullish(),
});

export type SkillLimits = z.infer<typeof skillLimitsSchema>;

export const jobProfileStructureTypeSchema = z.enum(['BULLET_POINTS', 'SENTENCES', 'TASKS']);

export type JobProfileStructureType = z.infer<typeof jobProfileStructureTypeSchema>;

export const jobProfileTemplateTypeSchema = z.enum(['COBRAINER', 'CUSTOM']);

export type JobProfileTemplateType = z.infer<typeof jobProfileTemplateTypeSchema>;

// --- Section structure ---

export const taskSectionSettingsSchema = z
    .object({
        expectedNumberOfSkillsPerTask: z.number().min(1).max(20),
        maximumNumberOfSkillsPerTask: z.number().min(1).max(20),
        contentGuidance: z.string().max(TASK_CONTENT_GUIDANCE_MAX_LENGTH).nullish(),
    })
    .refine(
        ({ expectedNumberOfSkillsPerTask, maximumNumberOfSkillsPerTask }) =>
            expectedNumberOfSkillsPerTask <= maximumNumberOfSkillsPerTask,
        {
            path: ['maximumNumberOfSkillsPerTask'],
        },
    );

export type TaskSectionSettings = z.infer<typeof taskSectionSettingsSchema>;

export const customJobProfileTemplateStructureSchema = z.object({
    name: translationTextSchema.array(),
    guidance: z.string(),
    type: jobProfileStructureTypeSchema,
    minNumberOfItems: z.number().min(0),
    maxNumberOfItems: z.number().min(0),
    // Present on TASKS sections only; the backend defaults it when omitted.
    taskSettings: taskSectionSettingsSchema.nullish(),
});

export type CustomJobProfileTemplateStructure = z.infer<typeof customJobProfileTemplateStructureSchema>;

// --- Section request/response ---

export const customJobProfileTemplateSectionRequestSchema = z.object({
    id: z.uuid(),
    structure: customJobProfileTemplateStructureSchema,
    type: jobProfileTemplateTypeSchema,
});

export type CustomJobProfileTemplateSectionRequest = z.infer<typeof customJobProfileTemplateSectionRequestSchema>;

export const customJobProfileTemplateSectionResponseSchema = z.object({
    id: z.uuid(),
    structure: customJobProfileTemplateStructureSchema,
    type: jobProfileTemplateTypeSchema,
});

export type CustomJobProfileTemplateSectionResponse = z.infer<typeof customJobProfileTemplateSectionResponseSchema>;

export const customJobProfileTemplateRequestSchema = z.object({
    cobrainerJobTemplateId: z.uuid().nullish(),
    name: z.string().max(512),
    description: z.string().max(5000),
    jobRoleSections: customJobProfileTemplateSectionRequestSchema.array().min(1),
    jobLevelSections: customJobProfileTemplateSectionRequestSchema.array().min(1),
    roleSkillLimits: skillLimitsSchema,
    levelSkillLimits: skillLimitsSchema,
});

export type CustomJobProfileTemplateRequest = z.infer<typeof customJobProfileTemplateRequestSchema>;

const userShortSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    emailAddress: z.string().optional(),
});

export const customJobProfileTemplateResponseSchema = z.object({
    id: z.uuid(),
    cobrainerJobTemplateId: z.uuid().nullable().optional(),
    name: z.string(),
    description: z.string(),
    jobArchitectureId: z.uuid(),
    createdBy: userShortSchema.nullable().optional(),
    createdAt: z.string(),
    jobRoleSections: customJobProfileTemplateSectionResponseSchema.array(),
    jobLevelSections: customJobProfileTemplateSectionResponseSchema.array(),
    roleSkillLimits: skillLimitsSchema,
    levelSkillLimits: skillLimitsSchema,
});

export type CustomJobProfileTemplateResponse = z.infer<typeof customJobProfileTemplateResponseSchema>;

export const customJobProfileTemplateGetResponseSchema = z.object({
    template: customJobProfileTemplateResponseSchema.nullable(),
});

export const managedJobProfileTemplateResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string(),
    activatedAt: z.string(),
    // Backend deploys ahead of the frontend, so this may be absent at runtime. Absent means not
    // compliant: the badge is only ever shown on an explicit positive from the backend.
    euPayTransparencyCompliant: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
    isDefault: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
    jobRoleSections: customJobProfileTemplateSectionResponseSchema.array(),
    jobLevelSections: customJobProfileTemplateSectionResponseSchema.array(),
    roleSkillLimits: skillLimitsSchema,
    levelSkillLimits: skillLimitsSchema,
});

export type ManagedJobProfileTemplateResponse = z.infer<typeof managedJobProfileTemplateResponseSchema>;

export const managedJobProfileTemplateResponseArraySchema = managedJobProfileTemplateResponseSchema.array();

export const jobProfileTemplatePreviewRequestSchema = z.object({
    jobRoleSections: customJobProfileTemplateSectionRequestSchema.array(),
    jobLevelSections: customJobProfileTemplateSectionRequestSchema.array(),
    roleSkillLimits: skillLimitsSchema,
    levelSkillLimits: skillLimitsSchema,
});

export type JobProfileTemplatePreviewRequest = z.infer<typeof jobProfileTemplatePreviewRequestSchema>;

export const previewStructuredSectionSchema = z.object({
    tag: z.string(),
    value: z.string(),
});

export const previewSkillSchema = z.object({
    id: z.string(),
    title: z.string(),
    skillType: z.enum(['hard', 'soft', 'language']),
    level: z.number().optional(),
    competencyId: z.string().optional(),
});

export const previewCompetencySchema = z.object({
    id: z.string(),
    title: z.string(),
});

export const previewJobItemSchema = jobProfileComponentSchema;

export const jobProfileTemplatePreviewResponseSchema = z.object({
    jobRole: previewJobItemSchema.optional(),
    jobLevel: previewJobItemSchema.optional(),
});

export type PreviewJobItem = z.infer<typeof previewJobItemSchema>;
export type PreviewSkill = z.infer<typeof previewSkillSchema>;
export type PreviewCompetency = z.infer<typeof previewCompetencySchema>;
export type JobProfileTemplatePreviewResponse = z.infer<typeof jobProfileTemplatePreviewResponseSchema>;
